import React, { useState, useEffect, useRef } from 'react';
import { MerchantStore } from '../MerchantModel';
import i18n from '@/utils/i18n';
import { Communicator } from '../../../tools/Communication';
import { LayoutStoreInstance } from '../../../LayoutStore';
import { Link } from 'react-router-dom';
import {
  CheckboxLabel,
  VytalCheckboxLabelContainer,
} from '../../../pages/AdminStoreDetail/Menu/ProductItem/ProductOrderSheet/ExtraSelector/ExtraGroupEditModal/styled';
import Checkbox from '../../../components/Checkbox';
import { EditItem, EditSection } from '../../../components/Layout';
import ClassicLoadingSpinner from '../../../components/LoadingSpinner/ClassicLoadingSpinner';
import { ContentSection } from '../../../components/Layout/ContentSection';

const getStoreForInfo = (
  vytalStoreGuid: string,
  externalPickpackId: string,
): MerchantStore => {
  return {
    id: vytalStoreGuid,
    merchantId: '',
    externalPickpackId: externalPickpackId,
    name: '',
    appUsers: [],
    storeManagers: [],
    imageIds: [],
    category: { id: 0, nameDe: '-', nameEn: '-' },
    openingHours: [],
    containerInformation: [],
    foodInfoTextDe: '',
    foodInfoTextEn: '',
    phoneNumber: '',
    websiteUrl: '',
    streetAddress: '',
    zipCode: '',
    city: '',
    country: '',
    latitude: 50.9278261,
    longitude: 6.9332776,
    contractedDate: '',
    onboardedDate: '',
    testPeriodEnd: '',
    sellsOfflineCards: false,
    amountOfflineCards: 0,
    region: 0,
  };
};

interface MerchantStorePageProps {
  vytalStoreGuid: string;
  externalPickpackId: string;
  title: string;
}

interface OfflineCardInfo {
  userId: string;
  signupCode: string;
  isActivated: boolean;
}

export const MerchantOfflineCardsTab: React.FC<MerchantStorePageProps> = ({
  vytalStoreGuid,
  externalPickpackId,
  title,
}) => {
  const saveMerchantPhoneInformationRef = useRef<(() => void) | null>(null);

  const [store, setStore] = useState<MerchantStore>(() =>
    getStoreForInfo(vytalStoreGuid, externalPickpackId),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [offlineCards, setOfflineCards] = useState<OfflineCardInfo[]>([]);
  const [finishedIntialLoad, setFinishedIntialLoad] = useState(false);

  const reloadMerchantStore = (storeId: string) => {
    return Communicator.Get('/Merchant/GetMerchantStore', { storeId })
      .then((response) => response.json())
      .then((store: MerchantStore) => {
        if (store) {
          setStore(store);
        }
      });
  };

  const reloadOfflineCards = (storeId: string) => {
    return Communicator.Get('/Merchant/GetLinkedOfflineCards', { storeId })
      .then((response) => response.json())
      .then((offlineCards: OfflineCardInfo[]) => {
        if (offlineCards) setOfflineCards(offlineCards);
      });
  };

  const onSaveClick = async (lowerUpperContainerInformation: any[] = []) => {
    setIsSaving(true);
    await Promise.all([
      saveStoreVytalInfoOnly(lowerUpperContainerInformation),
      // @ts-ignore
      saveMerchantPhoneInformationRef.current?.(),
    ]);

    setIsSaving(false);
  };

  const saveStoreVytalInfoOnly = (
    lowerUpperContainerInformation: any[] = [],
  ) => {
    return new Promise((resolve, reject) => {
      const data = {
        id: store.id,
        sellsOfflineCards: store.sellsOfflineCards,
        amountOfflineCards: store.amountOfflineCards,
        categoryId: store.category.id,
        containerInformation: store.containerInformation,
      };

      if (lowerUpperContainerInformation.length > 0) {
        for (const containerInfo of data.containerInformation) {
          const containerInfo2 = lowerUpperContainerInformation.find(
            (i) => i.containerTypeId == containerInfo.containerTypeId,
          );
          if (containerInfo2 != null) {
            containerInfo.storeLostCostLower =
              containerInfo2.storeLostCostLower;
            containerInfo.storeLostCostUpper =
              containerInfo2.storeLostCostUpper;
          }
        }
      }

      const convertContainerInfoInput = (
        input: string | null | undefined | number,
      ) => (typeof input === 'string' ? parseInt(input) : input);

      data.containerInformation = data.containerInformation.map(
        (containerInfo) => ({
          ...containerInfo,
          // when chaning the prices they are saved as strings => transfer them to numbers
          transactionPrice: parseFloat(
            String(containerInfo.transactionPrice) || '0',
          ),
          storeLostCost:
            convertContainerInfoInput(containerInfo.storeLostCost) || 0,
          storeLostCostUpper:
            convertContainerInfoInput(containerInfo.storeLostCostUpper) || 0,
          storeLostCostLower:
            convertContainerInfoInput(containerInfo.storeLostCostLower) || 0,
        }),
      );

      Communicator.PostData('/Merchant/UpdateMerchantStore', data)
        .then((data: any) => {
          LayoutStoreInstance.setSuccess(i18n.t('Merchants:storeSaved'));
          resolve(data);
        })
        .catch(() => {
          LayoutStoreInstance.setError(i18n.t('Merchants:errorSavingStore'));
          reject();
        });
    });
  };

  const renderOfflineCardsTable = () => {
    return (
      <div
        className="list-group mb-5"
        style={{
          maxHeight: '197px',
          overflow: 'scroll',
          marginTop: '1em',
          marginBottom: '1em',
        }}
      >
        <h4>{i18n.t('Merchants:linkedOfflineCards')}</h4>
        {offlineCards
          .filter((o) => !o.isActivated)
          .map((_) => (
            <Link
              key={_.userId}
              to={'/users/' + _.userId}
              className={'list-group-item list-group-item-action'}
            >
              {_.signupCode}
            </Link>
          ))}
      </div>
    );
  };

  // Effect for initial component mount
  useEffect(() => {
    Promise.all([
      reloadMerchantStore(vytalStoreGuid),
      reloadOfflineCards(vytalStoreGuid),
    ]).then(() => setFinishedIntialLoad(true));
  }, []);

  // Effect for when props change (equivalent to componentWillUpdate)
  useEffect(() => {
    setStore(getStoreForInfo(vytalStoreGuid, externalPickpackId));
    reloadMerchantStore(vytalStoreGuid);
  }, [vytalStoreGuid, externalPickpackId]);

  if (!finishedIntialLoad) {
    return <ClassicLoadingSpinner></ClassicLoadingSpinner>;
  }

  return (
    <ContentSection title={title} collapsable={false} key="content">
      <EditSection
        title={i18n.t('Merchants:assets')}
        isLoading={isSaving}
        onSave={onSaveClick.bind(null)}
      >
        <EditItem title={i18n.t('Merchants:offlineCards')}>
          <div>
            <VytalCheckboxLabelContainer>
              <Checkbox
                checked={store.sellsOfflineCards}
                vytalStyle
                onClick={() =>
                  setStore({
                    ...store,
                    sellsOfflineCards: !store.sellsOfflineCards,
                  })
                }
              />
              <CheckboxLabel
                onClick={() =>
                  setStore({
                    ...store,
                    sellsOfflineCards: !store.sellsOfflineCards,
                  })
                }
              >
                {i18n.t('Merchants:offlineCardSeller')}
              </CheckboxLabel>
            </VytalCheckboxLabelContainer>
          </div>
          <div className="input-group mt-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Merchants:offlineCards')}
            </span>
            <input
              type="text"
              className="form-control"
              disabled={true}
              value={offlineCards.length}
            />
          </div>
          {store.sellsOfflineCards && renderOfflineCardsTable()}
        </EditItem>
      </EditSection>
    </ContentSection>
  );
};
